import { useState, useEffect, useMemo } from "react";
import { Drawer, Button } from "antd";
import {
  CloseOutlined,
  CalendarOutlined,
  FileProtectOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  IdcardOutlined,
  FormOutlined,
  SettingOutlined
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";

const BLUE = "#1677ff";

const TAB_ITEMS = [
  { key: "appointments", label: "Appointments", icon: CalendarOutlined, path: "/", metricsLabel: 'List Appointments' },
  { key: "privacy", label: "Terms of Use & Privacy Policy", icon: FileProtectOutlined, path: "/privacy-policy" },
  { key: "faq", label: "FAQ", icon: QuestionCircleOutlined, path: "/faq" },
  { key: "users", label: "Manage Clinicians", icon: IdcardOutlined, path: "/admin/clinician", forAdmin: true },
  { key: "template-builder", label: "Template Builder", icon: FormOutlined, path: "/admin/templates", forAdmin: true },
  { key: "practice-defaults", label: "Practice Defaults", icon: SettingOutlined, path: "/admin/practice-defaults", forAdmin: true },
];

const pathToKey = (pathname) => {
  // The Template Builder tab covers both the library list and the editor routes.
  if (pathname.startsWith("/admin/templates") || pathname.startsWith("/admin/template-builder")) {
    return "template-builder";
  }
  return TAB_ITEMS.find((t) => t.path === pathname)?.key ?? null;
};

const SidebarDrawer = ({ options, open, onClose, width = 280 }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const derivedKey = useMemo(() => pathToKey(location.pathname), [location.pathname]);
  const [activeKey, setActiveKey] = useState(derivedKey);

  const isAdmin = useMemo(() => {
    return options?.roles?.includes?.('admin');
  }, [options])

  useEffect(() => {
    setActiveKey(derivedKey);
  }, [derivedKey]);

  const onSelect = (key) => {
    setActiveKey(key);
    const dest = TAB_ITEMS.find((t) => t.key === key)?.path;
    if (dest) {
      navigate(dest);
      onClose?.();
    }
  };

  return (
    <Drawer
      placement="left"
      open={open}
      onClose={onClose}
      width={width}
      closable={false}
      destroyOnClose={false}
      rootStyle={{ top: 80 }}
      maskStyle={{ top: 80 }}
      styles={{
        content: { background: "#000", color: "#fff" },
        header: { padding: "12px 16px", background: "#000", color: "#fff" },
        body: { padding: 0, background: "#000", color: "#fff", height: "calc(100vh - 80px)", overflow: "auto" },
      }}
      
    >
      <div
        role="tablist"
        aria-orientation="vertical"
        style={{
          padding: "12px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {TAB_ITEMS.map(({ key, label, icon: IconCmp, metricsLabel, forAdmin }) => {
          const isActive = key === activeKey;
          const color = isActive ? BLUE : "#fff";
          const fontWeight = isActive ? 600 : 400;

          if(forAdmin && !isAdmin) return <></>

          return (
            <button
              key={key}
              role="tab"
              metrics-label={metricsLabel}
              aria-selected={isActive}
              onClick={() => onSelect(key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                cursor: "pointer",
                padding: "10px 12px",
                borderRadius: 8,
                color,
                fontWeight,
              }}
            >
              <IconCmp style={{ color }} />
              <span style={{ color }}>{label}</span>
            </button>
          );
        })}
      </div>
    </Drawer>
  );
};

export default SidebarDrawer;
